import { useTreeEnvironment } from './ControlledTreeEnvironment';
import { useGetGetParentOfLinearItem } from './useGetParentOfLinearItem';
import { isOutsideOfContainer } from './layoutUtils';
import { useStableHandler } from '../use-stable-handler';
var getHoveringPosition = function (clientY, treeTop, itemHeight, linearItems, treeId, items, canDropOnFolder, canDropOnNonFolder, canReorderItems) {
    var hoveringPosition = (clientY - treeTop) / itemHeight;
    var treeLinearItems = linearItems[treeId];
    var linearIndex = Math.max(0, Math.floor(hoveringPosition));
    if (linearIndex > treeLinearItems.length - 1) {
        return {
            linearIndex: treeLinearItems.length - 1,
            targetItem: treeLinearItems[treeLinearItems.length - 1].item,
            offset: 'bottom',
            targetLinearItem: treeLinearItems[treeLinearItems.length - 1],
        };
    }
    var targetLinearItem = treeLinearItems[linearIndex];
    var targetItem = items[targetLinearItem.item];
    var offset;
    var lineThreshold = !canReorderItems
        ? 0
        : ((targetItem === null || targetItem === void 0 ? void 0 : targetItem.isFolder) && canDropOnFolder) || canDropOnNonFolder
            ? 0.2
            : 0.5;
    if (hoveringPosition % 1 < lineThreshold) {
        offset = 'top';
    }
    else if (hoveringPosition % 1 > 1 - lineThreshold) {
        offset = 'bottom';
    }
    return { linearIndex: linearIndex, offset: offset, targetItem: targetItem, targetLinearItem: targetLinearItem };
};
var useIsDescendant = function () {
    var getParentOfLinearItem = useGetGetParentOfLinearItem();
    var isDescendant = function (treeId, itemLinearIndex, potentialParents) {
        var _a = getParentOfLinearItem(itemLinearIndex, treeId), parentLinearIndex = _a.parentLinearIndex, parent = _a.parent;
        if (potentialParents.find(function (p) { return p.index === parent.item; })) {
            return true;
        }
        if (parent.depth === 0) {
            return false;
        }
        return isDescendant(treeId, parentLinearIndex, potentialParents);
    };
    return isDescendant;
};
export var useOnDragOverTreeHandler = function (lastDragCode, setLastDragCode, draggingItems, itemHeight, onDragAtPosition, onPerformDrag) {
    var _a = useTreeEnvironment(), canDropOnFolder = _a.canDropOnFolder, canDropOnNonFolder = _a.canDropOnNonFolder, canDragAndDrop = _a.canDragAndDrop, linearItems = _a.linearItems, items = _a.items, canReorderItems = _a.canReorderItems, trees = _a.trees;
    var getParentOfLinearItem = useGetGetParentOfLinearItem();
    var isDescendant = useIsDescendant();
    return useStableHandler(function (e, treeId, containerRef) {
        var _a, _b;
        if (!draggingItems) {
            return;
        }
        if (!canDragAndDrop) {
            return;
        }
        if (!containerRef.current) {
            return;
        }
        if (e.clientX < 0 || e.clientY < 0) {
            return; // TODO hotfix
        }
        var treeBb = containerRef.current.getBoundingClientRect();
        var outsideContainer = isOutsideOfContainer(e, treeBb);
        if (linearItems[treeId].length === 0) {
            // Empty tree
            onPerformDrag({
                targetType: 'root',
                treeId: treeId,
                depth: 0,
                linearIndex: 0,
                targetItem: trees[treeId].rootItem,
            });
            return;
        }
        var _c = getHoveringPosition(e.clientY, treeBb.top, itemHeight, linearItems, treeId, items, canDropOnFolder, canDropOnNonFolder, canReorderItems), linearIndex = _c.linearIndex, offset = _c.offset;
        var nextDragCode = outsideContainer
            ? 'outside'
            : "".concat(treeId).concat(linearIndex).concat(offset !== null && offset !== void 0 ? offset : '');
        if (lastDragCode === nextDragCode) {
            return;
        }
        setLastDragCode(nextDragCode);
        if (outsideContainer) {
            onDragAtPosition(undefined);
            return;
        }
        if (linearIndex < 0 || linearIndex >= linearItems[treeId].length) {
            onDragAtPosition(undefined);
            return;
        }
        var targetItem = linearItems[treeId][linearIndex];
        var redirectTargetToParent = !canReorderItems &&
            !canDropOnNonFolder &&
            !items[targetItem.item].isFolder;
        if (redirectTargetToParent) {
            var _d = getParentOfLinearItem(linearIndex, treeId), parentLinearIndex = _d.parentLinearIndex, parent_1 = _d.parent;
            targetItem = parent_1;
            linearIndex = parentLinearIndex;
        }
        if (isDescendant(treeId, linearIndex, draggingItems)) {
            onDragAtPosition(undefined);
            return;
        }
        var depth = targetItem.depth;
        var targetItemData = items[targetItem.item];
        if (!offset && !canDropOnNonFolder && !targetItemData.isFolder) {
            onDragAtPosition(undefined);
            return;
        }
        if (!offset && !canDropOnFolder && targetItemData.isFolder) {
            onDragAtPosition(undefined);
            return;
        }
        if (offset && !canReorderItems) {
            onDragAtPosition(undefined);
            return;
        }
        var parent = getParentOfLinearItem(linearIndex, treeId).parent;
        if (draggingItems.some(function (draggingItem) { return draggingItem.index === targetItem.item; })) {
            onDragAtPosition(undefined);
            return;
        }
        var newChildIndex = items[parent.item].children.indexOf(targetItem.item) +
            (offset === 'top' ? 0 : 1);
        if (offset === 'top' &&
            depth === ((_b = (_a = linearItems[treeId][linearIndex - 1]) === null || _a === void 0 ? void 0 : _a.depth) !== null && _b !== void 0 ? _b : -1)) {
            offset = 'bottom';
            linearIndex -= 1;
        }
        var draggingPosition;
        if (offset) {
            draggingPosition = {
                targetType: 'between-items',
                treeId: treeId,
                parentItem: parent.item,
                depth: targetItem.depth,
                linearIndex: linearIndex + (offset === 'top' ? 0 : 1),
                // childIndex: linearIndex - parentLinearIndex - 1 + (offset === 'top' ? 0 : 1),
                // childIndex: environment.items[parent.item].children!.indexOf(targetItem.item) + (offset === 'top' ? 0 : 1),
                childIndex: newChildIndex,
                linePosition: offset,
            };
        }
        else {
            draggingPosition = {
                targetType: 'item',
                treeId: treeId,
                parentItem: parent.item,
                targetItem: targetItem.item,
                depth: targetItem.depth,
                linearIndex: linearIndex,
            };
        }
        onPerformDrag(draggingPosition);
    });
};
